import { useReactTable } from "@tanstack/react-table";
import React, { useState } from "react";
import { Payment } from "./PaymentsPage";
import { Button, Checkbox, Select, Slider } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";

const PAYMENT_STATUS_FILTER_VALUES: Array<SelectOptionType> = [
  {
    value: "",
    label: "All statuses",
  }, 
  {
    value: "enabled",
    label: "Enabled",
  },
  {
    value: "disabled",
    label: "Disabled"
  }
]

const STATUS_FILTER_VALUES: Array<SelectOptionType> = [
  {
    value: "",
    label: "All statuses",
  },
  {
    value: "approved",
    label: "Approved",
  },
  {
    value: "pending_approval",
    label: "Pending Approval"
  }
]

const NOTIFICATONS_STATUS_VALUES: Array<SelectOptionType> = [
  {
    value: "",
    label: "All statuses",
  }, 
  {
    value: "enabled",
    label: "Enabled",
  },
  {
    value: "disabled",
    label: "Disabled"
  }
]

export type PaymentsFilterProps = {
  table: ReturnType<typeof useReactTable<Payment>>
  maxBatchTotalAmount: number,
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
}

export type Filters = {
  paymentEnabledStatus: string,
  approvalStatus: string,
  notifications: string,
  firstPaymentStart: string,
  firstPaymentEnd: string,
  lastPaymentStart: string,
  lastPaymentEnd: string,
  hasFailedPayments: boolean,
  batchTotalAmountRange: [number, number],
}

export const PaymentsFilter: React.FC<PaymentsFilterProps> = ({
  table, 
  maxBatchTotalAmount, 
  setActiveFilters,
  filters,
  setFilters
}) => {
  const applyFilters = () => {
    let activeFilters = 0;

    const paymentEnabledStatusCol = table.getColumn("paymentEnabledStatus")
    const statusCol = table.getColumn("status")
    const notificationsCol = table.getColumn("notifications")
    const hasFailedPaymentsCol = table.getColumn("failed")

    if (filters.paymentEnabledStatus !== "") activeFilters++
    paymentEnabledStatusCol?.setFilterValue(filters.paymentEnabledStatus || undefined)

    if (filters.approvalStatus !== "") activeFilters++;
    statusCol?.setFilterValue(filters.approvalStatus || undefined)

    if (filters.notifications !== "") activeFilters++
    notificationsCol?.setFilterValue(filters.notifications || undefined)

    if (filters.hasFailedPayments !== false) activeFilters++
    hasFailedPaymentsCol?.setFilterValue(filters.hasFailedPayments || undefined)

    const firstPaymentDateCol = table.getColumn("firstPaymentDate")

    if (filters.firstPaymentStart !== "" || filters.firstPaymentEnd !== "") activeFilters++
    firstPaymentDateCol?.setFilterValue(
      filters.firstPaymentStart || filters.firstPaymentEnd
        ? { firstPaymentStart: filters.firstPaymentStart, firstPaymentEnd: filters.firstPaymentEnd }
        : undefined
    )

    const lastPaymentDateCol = table.getColumn("lastPaymentDate")

    if (filters.lastPaymentStart !== "" || filters.lastPaymentEnd !== "") activeFilters++
    lastPaymentDateCol?.setFilterValue(
      filters.lastPaymentStart || filters.lastPaymentEnd
        ? { lastPaymentStart: filters.lastPaymentStart, lastPaymentEnd: filters.lastPaymentEnd }
        : undefined
    )

    if (filters.batchTotalAmountRange[0] !== 0 || filters.batchTotalAmountRange[1] !== maxBatchTotalAmount) activeFilters++
    const batchTotalAmount = table.getColumn("totalAmount")
    batchTotalAmount?.setFilterValue(filters.batchTotalAmountRange)

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      paymentEnabledStatus: "",
      approvalStatus: "",
      notifications: "",
      firstPaymentStart: "",
      firstPaymentEnd: "",
      lastPaymentStart: "",
      lastPaymentEnd: "",
      hasFailedPayments: false,
      batchTotalAmountRange: [0, maxBatchTotalAmount]
    })
    setActiveFilters(0)
    table.resetColumnFilters()
  }

  const getPaymentStatusOption = (value: string) => (PAYMENT_STATUS_FILTER_VALUES.find((v) => v.value === value))
  const getStatusOption = (value: string) => (STATUS_FILTER_VALUES.find((v) => v.value === value))
  const getNotificationOption = (value: string) => (NOTIFICATONS_STATUS_VALUES.find((v) => v.value === value))

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        <div>
          <Select 
            className="min-h-10" 
            options={PAYMENT_STATUS_FILTER_VALUES} 
            description="Filter by payment enabled status" 
            label="Payment Status" 
            labelLocation="above"
            value={getPaymentStatusOption(filters.paymentEnabledStatus)} 
            onChange={(value) => setFilters(f => ({... f, paymentEnabledStatus: value.value}))}
          />
        </div>
        
        <div>
          <Select 
            className="min-h-10" 
            options={STATUS_FILTER_VALUES} 
            description="Filter by approval status" 
            label="Approval Status" 
            labelLocation="above"
            value={getStatusOption(filters.approvalStatus)} 
            onChange={(value) => setFilters(f => ({... f, approvalStatus: value.value}))}
          />
        </div>

        <div>
          <Select 
            className="min-h-10" 
            options={NOTIFICATONS_STATUS_VALUES} 
            description="Filter by notification status" 
            label="Notifications" 
            labelLocation="above" 
            value={getNotificationOption(filters.notifications)} 
            onChange={(value) => setFilters(f => ({... f, notifications: value.value}))}
          />
        </div>

        <div>
          <label className="block text-sm/6 font-medium text-gray-900 mb-1">First Payment Date</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="First Payment Date - From"
              type="date"
              value={filters.firstPaymentStart}
              onChange={(e) => setFilters(f => ({ ...f, firstPaymentStart: e.target.value }))}
              className="min-height-10 border px-3 py-2 rounded-md text-sm shadow-sm"
            />
            <span className="text-gray-700">to</span>
            <input
              aria-label="First Payment Date - To"
              type="date"
              value={filters.firstPaymentEnd}
              onChange={(e) => setFilters(f => ({ ...f, firstPaymentEnd: e.target.value }))}
              className="min-h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm/6 font-medium text-gray-900 mb-1">Last Payment Date</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Last Payment Date - From"
              type="date"
              value={filters.lastPaymentStart}
              onChange={(e) => setFilters(f => ({ ...f, lastPaymentStart: e.target.value }))}
              className="min-height-10 border px-3 py-2 rounded-md text-sm shadow-sm"
            />
            <span className="text-gray-700">to</span>
            <input
              aria-label="Last Payment Date - To"
              type="date"
              value={filters.lastPaymentEnd}
              onChange={(e) => setFilters(f => ({ ...f, lastPaymentEnd: e.target.value }))}
              className="min-h-10 border px-3 py-2 rounded-md text-sm shadow-sm"
            />
          </div>
        </div>

      </div>
      <div className="pt-4 max-w-96">
        <div className="block text-sm/6 font-medium text-gray-900 mb-1">Batch Total Payment Amount</div>
        <Slider 
          label={"Batch Payment Amount"}
          isRange 
          min={0} 
          max={maxBatchTotalAmount} 
          value={filters.batchTotalAmountRange}
          onChange={(v) => {
            if (Array.isArray(v) && v.length == 2) {
              setFilters(f => ({...f, batchTotalAmountRange: v}))
            }
          }}
        />
      </div>

      <div className="pt-4">
        <Checkbox checked={filters.hasFailedPayments} onChange={(checked) => { setFilters((f) => ({ ...f, hasFailedPayments: checked ? true : false })) }} label="Has failed payments"/>
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>Reset Filters</Button>
          <Button onClick={applyFilters}>Apply Filters</Button>
        </div>
      </div>  
    </div>
  )
}